import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type ZoneSummary = { green: number; amber: number; red: number; neutral: number };

type PropertySummary = {
  id: string;
  name: string;
  role: 'owner' | 'member';
  archivedAt: string | null;
  archivedReason: 'tier_expired' | 'owner_archived' | 'downgrade_autoarchive' | null;
  zoneSummary: ZoneSummary;
  mapImageUrl: string | null;
};

type DashboardEquipment = {
  id: string;
  name: string;
  archivedAt: string | null;
  isInsideSeason: boolean;
  isCheckInOverdue: boolean;
  nextCheckInDueAt: string | null;
  nextMaintenanceDueAt: string | null;
  notOkFlagCount: number;
  outOfCommissionAt: string | null;
};

export const Route = createFileRoute('/app/dashboard')({
  component: Dashboard,
});

const ZONE_DOT: Record<keyof ZoneSummary, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-400',
  red: 'bg-red-500',
  neutral: 'bg-gray-300',
};

function ZoneDots({ summary }: { summary: ZoneSummary }) {
  const total = summary.green + summary.amber + summary.red + summary.neutral;
  if (total === 0) return <span className="text-xs text-muted-foreground">No zones</span>;
  return (
    <div className="flex items-center gap-3">
      {(['red', 'amber', 'green', 'neutral'] as const).map((state) =>
        summary[state] > 0 ? (
          <span key={state} className="flex items-center gap-1 text-xs text-muted-foreground">
            <span className={cn('w-2 h-2 rounded-full inline-block shrink-0', ZONE_DOT[state])} />
            {summary[state]}
          </span>
        ) : null,
      )}
    </div>
  );
}

function relativeDate(iso: string): string {
  const diffDays = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'tomorrow';
  return `in ${diffDays}d`;
}

function EquipmentAlerts({ equipment }: { equipment: DashboardEquipment[] }) {
  const alerts = equipment.filter(
    (eq) =>
      eq.archivedAt === null &&
      !eq.outOfCommissionAt &&
      eq.isInsideSeason &&
      (eq.isCheckInOverdue ||
        eq.notOkFlagCount > 0 ||
        (eq.nextMaintenanceDueAt !== null &&
          new Date(eq.nextMaintenanceDueAt).getTime() - Date.now() < 7 * 86400000)),
  );

  if (alerts.length === 0) return null;

  return (
    <div className="mt-2 pt-2 border-t border-border/60 flex flex-col gap-1">
      {alerts.map((eq) => {
        const issues: string[] = [];
        if (eq.isCheckInOverdue) issues.push('check-in overdue');
        if (eq.notOkFlagCount > 0)
          issues.push(`${eq.notOkFlagCount} flag issue${eq.notOkFlagCount > 1 ? 's' : ''}`);
        if (eq.nextMaintenanceDueAt) {
          const label = relativeDate(eq.nextMaintenanceDueAt);
          issues.push(`maintenance ${label}`);
        }
        return (
          <div key={eq.id} className="flex items-start gap-1.5 text-xs">
            <span className="text-amber-500 shrink-0 mt-px">⚠</span>
            <span>
              <span className="font-medium">{eq.name}</span>
              <span className="text-muted-foreground"> — {issues.join(' · ')}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function PropertyCard({ p }: { p: PropertySummary }) {
  const { data: equipment } = useQuery({
    queryKey: ['equipment', p.id, false],
    queryFn: () =>
      api
        .get<{ equipment: DashboardEquipment[] }>(`/v1/properties/${p.id}/equipment`)
        .then((r) => r.equipment),
    enabled: !p.archivedAt,
  });

  const hasOverdueCheckIn = (equipment ?? []).some(
    (eq) =>
      eq.archivedAt === null &&
      !eq.outOfCommissionAt &&
      eq.isInsideSeason &&
      eq.isCheckInOverdue,
  );

  return (
    <Link
      to="/app/$propertyId"
      params={{ propertyId: p.id }}
      className="block rounded-lg border border-border bg-card p-4 hover:bg-accent transition-colors"
    >
      <div className="flex items-start gap-3">
        {p.mapImageUrl && (
          <span className="relative shrink-0">
            <img src={p.mapImageUrl} alt="" className="h-16 w-24 rounded-md object-cover bg-muted" />
            {hasOverdueCheckIn && (
              <span
                title="Equipment check-in overdue"
                className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-card"
              />
            )}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium">{p.name}</span>
            <span className="text-xs text-muted-foreground capitalize">{p.role}</span>
          </div>
          <ZoneDots summary={p.zoneSummary} />
        </div>
      </div>
      {!p.archivedAt && equipment && <EquipmentAlerts equipment={equipment} />}
    </Link>
  );
}

function Dashboard() {
  const [showArchived, setShowArchived] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [cadence, setCadence] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data: properties, isPending, error } = useQuery({
    queryKey: ['properties', showArchived],
    queryFn: () =>
      api.get<PropertySummary[]>(
        showArchived ? '/v1/properties?includeArchived=true' : '/v1/properties',
      ),
  });

  const create = useMutation({
    mutationFn: (body: { name: string; defaultMowCadenceDays?: number }) =>
      api.post<{ property: { id: string } }>('/v1/properties', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['properties'] });
      setShowForm(false);
      setName('');
      setCadence('');
      setFormError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'tier_cap_exceeded') {
        const d = err.details as { currentCount: number; cap: number; tier: string } | undefined;
        setFormError(
          d
            ? `Property cap reached (${d.currentCount}/${d.cap}) on your ${d.tier} plan.`
            : 'Property cap reached for your current plan.',
        );
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    const trimmed = name.trim();
    if (!trimmed) return;
    const body: { name: string; defaultMowCadenceDays?: number } = { name: trimmed };
    const cadenceNum = parseInt(cadence, 10);
    if (cadence !== '' && !isNaN(cadenceNum)) {
      body.defaultMowCadenceDays = cadenceNum;
    }
    create.mutate(body);
  }

  const active = properties?.filter((p) => p.archivedAt === null) ?? [];
  const archived = properties?.filter((p) => p.archivedAt !== null) ?? [];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold">Properties</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? 'Hide archived' : 'Show archived'}
          </Button>
          <Button size="sm" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Cancel' : 'Create Property'}
          </Button>
        </div>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="mb-6 rounded-lg border border-border bg-card p-4 flex flex-col gap-3"
        >
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="prop-name">
              Name
            </label>
            <input
              id="prop-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="prop-cadence">
              Default cadence (days)
            </label>
            <input
              id="prop-cadence"
              type="number"
              min={1}
              max={365}
              placeholder="7"
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          {formError && <p className="text-sm text-destructive">{formError}</p>}
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </form>
      )}

      {isPending && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">Failed to load properties.</p>}

      {!isPending && !error && (
        <>
          {active.length === 0 ? (
            <p className="text-muted-foreground">No properties yet. Create one to get started.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {active.map((p) => (
                <PropertyCard key={p.id} p={p} />
              ))}
            </div>
          )}

          {showArchived && archived.length > 0 && (
            <details className="mt-6">
              <summary className="cursor-pointer text-sm text-muted-foreground select-none">
                Archived ({archived.length})
              </summary>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 opacity-60">
                {archived.map((p) => (
                  <PropertyCard key={p.id} p={p} />
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}
